import { useEffect, useRef } from "react";
import { queryClient } from "@/lib/providers/query";
import { useSessionProvider } from "@/lib/providers/session/use-session";
import {
  WebSocketProvider as BaseWebSocketProvider,
  useWebSocketContext,
} from "@/lib/websocket/context";
import { setupQuerySync } from "@/lib/websocket/query-sync";

type WebSocketProviderProps = React.PropsWithChildren;

/** Internal component that sets up query sync after WebSocket is connected */
function QuerySyncSetup() {
  const { client, isConnected } = useWebSocketContext();
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!client || !isConnected) {
      return;
    }

    // Setup query sync when connected
    cleanupRef.current = setupQuerySync(client, queryClient);

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [client, isConnected]);

  return null;
}

/**
 * WebSocket Provider that integrates with session authentication Only connects when user is
 * authenticated
 */
export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const { session } = useSessionProvider();
  const isAuthenticated = session.data?.user?.id != null;

  // Don't render WebSocket provider if not authenticated
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // Get backend URL for WebSocket connection
  const backendUrl = import.meta.env.VITE_BACKEND_URL || "https://api.heyboomer.localhost";

  return (
    <BaseWebSocketProvider
      options={{
        url: backendUrl,
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
      }}
    >
      <QuerySyncSetup />
      {children}
    </BaseWebSocketProvider>
  );
}
